import { FC, useEffect, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Organization } from '@robocorp/robocloud-internal-cds-sdk';
import { useSnackbar } from '@robocorp/components';
import { IconWarningTriangle } from '@robocorp/icons/iconic';
import {
  isOrgBillingNotPaid,
  isOrgDisabled,
  isOrgOnInactiveTier,
  isOrgOutOfAssistantRuns,
  isOrgOutOfRunMinutes,
} from '~/utils/organizationWarningHelper';
import { useParams } from '~/hooks';
import { useOrganizationByShortnameQuery } from '../queries';

enum WarningType {
  Disabled,
  NotPaid,
  OutOfRunMinutes,
  OutOfActivityRuns,
  OnInactivePlan,
}

const getWarningType = (organization: Organization): WarningType | undefined => {
  if (isOrgDisabled(organization.state)) {
    return WarningType.Disabled;
  }
  if (isOrgBillingNotPaid(organization.billing_state)) {
    return WarningType.NotPaid;
  }
  if (isOrgOutOfRunMinutes(organization.rmin_state)) {
    return WarningType.OutOfRunMinutes;
  }
  if (isOrgOutOfAssistantRuns(organization.assrun_state)) {
    return WarningType.OutOfActivityRuns;
  }
  if (isOrgOnInactiveTier(organization.subscription_tier_id)) {
    return WarningType.OnInactivePlan;
  }
  return undefined;
};

const OrganizationActivityChecker: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { addSnackbar, closeSnackbar } = useSnackbar();
  const { organizationShortName } = useParams<'organizationShortName'>();

  const { data: currentOrganization } = useOrganizationByShortnameQuery({ organizationShortName });

  const snackType = currentOrganization && getWarningType(currentOrganization);
  const organizationName = currentOrganization?.name;

  const addSnackbarMessage = useCallback(
    ({ message, action: messageActions }: { message: ReactNode; action?: (key: string) => void }) =>
      addSnackbar({
        message,
        icon: IconWarningTriangle,
        variant: 'danger',
        persist: true,
        unique: true,
        close: true,
        messageActions,
      }),
    [addSnackbar],
  );

  /**
   * Show one snackbar message or action at a time (by importance)
   */
  const addWarningMessage = useCallback(
    ({ type, name, shortName }: { type: WarningType; name: string; shortName: string }) => {
      switch (type) {
        case WarningType.Disabled:
          return addSnackbarMessage({
            message: t('organizationDisabledSnack', { name }),
          });
        case WarningType.NotPaid:
          return addSnackbarMessage({
            message: t('organizationNotPaidSnack', { name }),
            action: (key: string) => {
              navigate(`/${shortName}/settings/billing/billing-info`);
              closeSnackbar(key);
            },
          });
        case WarningType.OutOfRunMinutes:
          return addSnackbarMessage({
            message: t('organizationExceededMinutesSnack', { name }),
            action: (key: string) => {
              navigate(`/${shortName}/settings/billing/cost-management`);
              closeSnackbar(key);
            },
          });
        case WarningType.OutOfActivityRuns:
          return addSnackbarMessage({
            message: t('organizationExceededAssistantRunsSnack', { name }),
            action: (key: string) => {
              navigate(`/${shortName}/settings/billing/cost-management`);
              closeSnackbar(key);
            },
          });
        case WarningType.OnInactivePlan:
          return addSnackbarMessage({
            message: t('organizationOnInactivePlanSnack', { name }),
            action: (key: string) => {
              navigate(`/${shortName}/settings/billing`);
              closeSnackbar(key);
            },
          });
        default:
          return undefined;
      }
    },
    [t, navigate, addSnackbarMessage, closeSnackbar],
  );

  useEffect(() => {
    const key =
      snackType && organizationName && organizationShortName
        ? addWarningMessage({
            type: snackType,
            name: organizationName,
            shortName: organizationShortName,
          })
        : undefined;

    return () => {
      if (key) {
        closeSnackbar(key);
      }
    };
  }, [snackType, organizationName, organizationShortName]);

  return null;
};

export default OrganizationActivityChecker;
